import { AccountHolderDto, AccountStatusDto, AddressDto } from './accountHolders.dtos';
import { PhoneNumberDto } from './common.dtos';

export enum AccountTypeDto {
  Individual = 'individual',
  Joint = 'joint',
  Retirement = 'retirement',
  Ugma = 'ugma',
  Entity = 'entity',
}

export enum JointAccountTypeDto {
  Community = 'Community',
  Survivor = 'Survivor',
  TenantsInCommon = 'TenantsInCommon',
  TenantsInEntirety = 'TenantsInEntirety',
}

export enum RetirementAccountTypeDto {
  Traditional = 'Traditional',
  Simple = 'Simple',
  SEP = 'SEP',
  Roth = 'Roth',
  Rollover = 'Rollover',
  RothBeneficiary = 'RothBeneficiary',
  CoverDell = 'CoverDell',
  Beneficiary = 'Beneficiary',
}

export enum EntityAccountTypeDto {
  CCorporation = 'CCorporation',
  SCorporation = 'SCorporation',
  LlcCCorporation = 'LlcCCorporation',
  LlcSCorporation = 'LlcSCorporation',
  LlcPartnership = 'LlcPartnership',
  PersonalTrust = 'PersonalTrust',
  BusinessTrust = 'BusinessTrust',
  Partnership = 'Partnership',
}

export enum BeneficiaryTypeDto {
  Individual = 'Individual',
  Trust = 'Trust',
}

export enum BeneficiaryAccountTypeDto {
  Primary = 'Primary',
  Contingent = 'Contingent',
}

export enum BeneficiraryRelationshipDto {
  Spouse = 'Spouse',
  Child = 'Child',
  Parent = 'Parent',
  Other = 'Other',
}

export interface AccountBeneficiaryDto {
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
  email: string;
  phones: Array<PhoneNumberDto>;
  dateOfBirth: string;
  ssid: string;
  maskedSsid: string;
  percent: number;
  address: AddressDto;
  relationship: BeneficiraryRelationshipDto;
  beneficiaryAccountType: BeneficiaryAccountTypeDto;
  beneficiaryType: BeneficiaryTypeDto;
}

export interface EntityInformationDto {
  name: string;
  taxId: string;
  title?: string;
  phoneNumber?: string;
  officeLocations?: string;
  primaryBankName?: string;
  businessNature?: string;
  address: AddressDto;
  createdAt: string;
  updatedAt?: string;
}

export enum AccountLiquidityNeedsDto {
  NotImportant = 'notImportant',
  SomewhatImportant = 'somewhatImportant',
  VeryImportant = 'veryImportant',
}

export enum AccountInvestmentObjectivesDto {
  CapitalPreservation = 'capitalPreservation',
  Income = 'income',
  Growth = 'growth',
  GrowthAndIncome = 'growthAndIncome',
  Speculation = 'speculation',
}

export enum AccountRiskToleranceDto {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
}

export enum AccountTimeHorizonDto {
  Short = 'short',
  Average = 'average',
  Long = 'long',
}

export enum AccountTradesPerYearDto {
  OneToFive = 'OneToFive',
  SixToTen = 'SixToTen',
  ElevenToTwenty = 'ElevenToTwenty',
  MoreThanTwenty = 'MoreThanTwenty',
}

export enum AccountYearsOfExperienceDto {
  One = 'One',
  TwoToThree = 'TwoToThree',
  FourToNine = 'FourToNine',
  TenOrMore = 'TenOrMore',
}

export interface AccountFinancialInvestmentExperienceTypeDto {
  yearsOfExperience: AccountYearsOfExperienceDto;
  tradesPerYear: AccountTradesPerYearDto;
}

export interface AccountInvestmentExperienceDto {
  stocks?: AccountFinancialInvestmentExperienceTypeDto;
  options?: AccountFinancialInvestmentExperienceTypeDto;
  commodities?: AccountFinancialInvestmentExperienceTypeDto;
  bonds?: AccountFinancialInvestmentExperienceTypeDto;
  margins?: AccountFinancialInvestmentExperienceTypeDto;
}

export interface AccountSuitabilityInformationDto {
  liquidityNeeds: AccountLiquidityNeedsDto;
  investmentObjectives: AccountInvestmentObjectivesDto;
  riskTolerance: AccountRiskToleranceDto;
  timeHorizon: AccountTimeHorizonDto;
  financialInvestmentExperience?: AccountInvestmentExperienceDto;
}

export enum AccountAnnualIncomeDto {
  LessThan25K = 'lessThan25K',
  Between25KAnd50K = 'between25KAnd50K',
  Between50KAnd100K = 'between50KAnd100K',
  Between100KAnd200K = 'between100KAnd200K',
  Between200KAnd300K = 'between200KAnd300K',
  Between300KAnd500K = 'between300KAnd500K',
  Between500KAnd1Point2M = 'between500KAnd1Point2M',
  MoreThan1Point2M = 'moreThan1Point2M',
}

export enum AccountTotalNetWorthDto {
  LessThan50K = 'lessThan50K',
  Between50KAnd100K = 'between50KAnd100K',
  Between100KAnd200K = 'between100KAnd200K',
  Between200KAnd500K = 'between200KAnd500K',
  Between500KAnd1M = 'between500KAnd1M',
  Between1MAnd5M = 'between1MAnd5M',
  MoreThan5M = 'moreThan5M',
}

export enum AccountLiquidNetWorthDto {
  LessThan50K = 'lessThan50K',
  Between50KAnd100K = 'between50KAnd100K',
  Between100KAnd200K = 'between100KAnd200K',
  Between200KAnd500K = 'between200KAnd500K',
  Between500KAnd1M = 'between500KAnd1M',
  Between1MAnd5M = 'between1MAnd5M',
  MoreThan5M = 'moreThan5M',
}

export enum AccountTaxBracketDto {
  Bottom = 'bottom',
  Middle = 'middle',
  Top = 'top',
}

export interface AccountFinancialAssetsDto {
  annualIncome?: AccountAnnualIncomeDto;
  totalNetWorth?: AccountTotalNetWorthDto;
  liquidNetWorth?: AccountLiquidNetWorthDto;
  taxBracket?: AccountTaxBracketDto;
}

export interface AccountDto {
  id: string;
  accountId?: number;
  accountNumber?: string;
  status?: AccountStatusDto;
  type: AccountTypeDto;
  isRoot: boolean;
  isAccredited?: boolean;
  primaryAccountHolder?: AccountHolderDto;
  secondaryAccountHolder?: AccountHolderDto;
  jointAccountType?: JointAccountTypeDto;
  jointCommunityPropertyState?: string;
  retirementAccountType?: RetirementAccountTypeDto;
  simpleIraPlanName?: string;
  beneficiaryIraDecedentName?: string;
  beneficiaries?: Array<AccountBeneficiaryDto>;
  entityAccountType?: EntityAccountTypeDto;
  applicationId?: string;
  otherAccountHolders?: Array<AccountHolderDto>;
  entityInformation?: EntityInformationDto;
  suitabilityInformation?: AccountSuitabilityInformationDto;
  financialAssets?: AccountFinancialAssetsDto;
  failedCreateAttempts?: number;
  lastFailedCreateAttemptError?: string;
  lastFailedCreateAttemptAt?: string;
  failedLinkAttempts?: number;
  lastFailedLinkAttemptError?: string;
  lastFailedLinkAttemptAt?: string;
  createdAt: string;
}

export interface BaseAccountParamsDto {
  id?: string;
}

export interface CreateAccountBodyDto {
  type: AccountTypeDto;
  jointAccountType?: JointAccountTypeDto;
  jointCommunityPropertyState?: string;
  retirementAccountType?: RetirementAccountTypeDto;
  simpleIraPlanName?: string;
  beneficiaryIraDecedentName?: string;
}

export interface CreateAccountDto {
  body: CreateAccountBodyDto;
}

export interface GetAccountDto {
  params: BaseAccountParamsDto;
}

export interface PatchAccountBodyDto extends CreateAccountBodyDto {}

export interface PatchAccountDto {
  params: BaseAccountParamsDto;
  body: PatchAccountBodyDto;
}

export interface PatchJointAccountInterestBodyDto {
  primaryAccountHolder: {
    percent: number;
  };
  secondaryAccountHolder: {
    percent: number;
  };
}

export interface PatchJointAccountInterestDto {
  params: BaseAccountParamsDto;
  body: PatchJointAccountInterestBodyDto;
}

export interface BaseAccountBeneficiaryDto extends Omit<AccountBeneficiaryDto, 'maskedSsid'> {}

export type PatchAccountBeneficiariesBodyDto = BaseAccountBeneficiaryDto[];

export interface PatchAccountBeneficiariesDto {
  params: BaseAccountParamsDto;
  body: PatchAccountBeneficiariesBodyDto;
}

export interface UpdateAccountSuitabilityInformationBodyDto {
  liquidityNeeds?: AccountLiquidityNeedsDto;
  investmentObjectives?: AccountInvestmentObjectivesDto;
  riskTolerance?: AccountRiskToleranceDto;
  timeHorizon?: AccountTimeHorizonDto;
  financialInvestmentExperience?: AccountInvestmentExperienceDto;
}

export interface UpdateAccountSuitabilityInformationDto {
  params: BaseAccountParamsDto;
  body: UpdateAccountSuitabilityInformationBodyDto; // NOTE: intentially setting patch for body because in the future 'create' will be removed
}

export interface UpdateAccountFinancialAssetsBodyDto {
  annualIncome?: AccountAnnualIncomeDto;
  totalNetWorth?: AccountTotalNetWorthDto;
  liquidNetWorth?: AccountLiquidNetWorthDto;
  taxBracket?: AccountTaxBracketDto;
}

export interface UpdateAccountFinancialAssetsDto {
  params: BaseAccountParamsDto;
  body: UpdateAccountFinancialAssetsBodyDto;
}

export interface SubmitAccountBodyDto {
  signature: {
    primaryAccountHolder?: string;
    secondaryAccountHolder?: string;
  };
}

export interface SubmitAccountDto {
  params: Partial<BaseAccountParamsDto>;
  body?: SubmitAccountBodyDto;
}

export interface LinkAccountBodyDto {
  id: string;
  primaryAccountHolder: {
    ssid: string;
    dateOfBirth: string;
  };
}

export interface LinkAccountDto {
  params: BaseAccountParamsDto;
  body: LinkAccountBodyDto;
}

export interface SwitchAccountDto {
  params: BaseAccountParamsDto;
}

export interface UpdateAccountSignatureBodyDto {
  primaryAccountHolder: {
    signature: string;
  };
  secondaryAccountHolder?: {
    signature: string;
  };
}

export interface UpdateAccountSignatureDto {
  params: BaseAccountParamsDto;
  body: UpdateAccountSignatureBodyDto;
}
